import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../shared/utils/api';
import DrillCard from './DrillCard';
import DrillSummary from './DrillSummary';

export default function DrillSession({ config, onExit }) {
  const [questions, setQuestions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const questionStartTime = useRef(null);

  useEffect(() => {
    const body = {
      mode: config.mode,
      question_type: 'drill',
      count: config.count || 10,
    };
    if (config.module && config.module !== 'both') body.module = config.module;
    if (config.topicIds && config.topicIds.length > 0) body.topic_ids = config.topicIds;

    api('/api/questions/practice', {
      method: 'POST',
      body: JSON.stringify(body),
    })
      .then((data) => {
        if (!data.questions || data.questions.length === 0) {
          setError(
            "No drills available for this selection. Try a different topic or 'Weakest topics' mode."
          );
          setLoading(false);
          return;
        }
        setQuestions(data.questions);
        setSessionId(data.session_id);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load drills. Please try again.');
        setLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    questionStartTime.current = Date.now();
  }, [currentIdx]);

  const submitAnswer = useCallback(
    async (selectedLetter) => {
      const question = questions[currentIdx];
      if (!question) return;
      const responseTime = questionStartTime.current
        ? (Date.now() - questionStartTime.current) / 1000
        : null;

      let newAnswer;
      try {
        const result = await api('/api/questions/practice/answer', {
          method: 'POST',
          body: JSON.stringify({
            question_id: question.id,
            selected_answer: selectedLetter,
            response_time_seconds: responseTime ? Math.round(responseTime) : null,
            session_id: sessionId,
          }),
        });
        newAnswer = { ...result, response_time_seconds: responseTime };
      } catch {
        newAnswer = {
          correct: false,
          correct_answer: '?',
          explanation: 'Could not record answer.',
          distractor_misconceptions: null,
          selected_answer: selectedLetter,
          response_time_seconds: responseTime,
        };
      }
      setAnswers((prev) => {
        const next = [...prev];
        next[currentIdx] = newAnswer;
        return next;
      });
    },
    [currentIdx, questions, sessionId]
  );

  const goNext = () => {
    if (currentIdx + 1 >= questions.length) {
      setDone(true);
    } else {
      setCurrentIdx((i) => i + 1);
    }
  };

  if (loading) {
    return <div className="text-sm text-text-muted py-sp-10 text-center">Loading drills…</div>;
  }

  if (error) {
    return (
      <div className="bg-bg-card rounded-r-xl border border-border-soft px-sp-6 py-sp-10 text-center">
        <p className="text-base font-semibold text-text-primary mb-sp-2">No drills available</p>
        <p className="text-sm text-text-muted max-w-sm mx-auto mb-sp-4">{error}</p>
        <button
          onClick={() => onExit()}
          className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors"
        >
          Back to setup
        </button>
      </div>
    );
  }

  if (done) {
    return (
      <DrillSummary
        questions={questions}
        answers={answers}
        onStartAnother={() => onExit()}
      />
    );
  }

  const question = questions[currentIdx];
  const total = questions.length;
  const result = answers[currentIdx] || null;
  const answered = !!result;
  const atEnd = currentIdx + 1 >= total;

  const segments = questions.map((_, i) => {
    const a = answers[i];
    if (i === currentIdx && !a) return 'bg-accent';
    if (a) return a.correct ? 'bg-green' : 'bg-red';
    return 'bg-border-soft';
  });

  return (
    <>
      <div className="flex items-center justify-end mb-sp-3">
        <span className="text-sm font-medium text-text-muted">
          {currentIdx + 1} / {total}
        </span>
      </div>

      <div className="flex gap-[3px] mb-sp-4">
        {segments.map((seg, i) => (
          <div key={i} className={`flex-1 h-[3px] rounded-[2px] ${seg}`} />
        ))}
      </div>

      <DrillCard
        question={question}
        result={result}
        onSelect={submitAnswer}
        onNext={goNext}
        atEnd={atEnd}
      />

      {!answered && (
        <div className="mt-sp-4 flex justify-end">
          <button
            onClick={() => onExit()}
            className="text-sm text-text-muted hover:text-text-primary transition-colors"
          >
            End session
          </button>
        </div>
      )}
    </>
  );
}
